import { useMemo, useState } from "react";

import Header from "./Header";

import { useAtom } from "jotai";
import { mapQueryAtom } from "./State";
import { mapDataAtom, poisAtom } from "./State";
import { mapOptionsAtom } from "./State";
import { selectedEntityAtom } from "./State";
import { useLocation, useNavigate } from "react-router-dom";
import { ChargingType, Entity } from "@the-real-insight/tri-model";
import { useTranslation } from 'react-i18next';
import MapAndImage from "./MapAndImage";
import { Button } from "@/components/ui/button";

export default function MapView() {
    const { t } = useTranslation();
    const [, setObjectPanelExpanded] = useState(false);
    const [pois, setPoIs] = useAtom(poisAtom);
    const [_selectedEntity, setSelectedEntity] = useAtom(selectedEntityAtom) as any;
    const [entityHistory, setEntityHistory] = useState([]) as any;
    const [_entityHistoryIndex, setEntityHistoryIndex] = useState(0) as any;
    const [mapQuery] = useAtom(mapQueryAtom);
    const [mapData, setMapData] = useAtom(mapDataAtom);
    const [mapOptions, setMapOptions] = useAtom(mapOptionsAtom) as any;
    const { search } = useLocation();
    const navigate = useNavigate();
    const searchParams = useMemo(() => new URLSearchParams(search), [search]);

    const selectEntity = (entity: any, changeHistory: boolean = true) => {
        console.log('Set entity >>>', entity);
        setSelectedEntity(entity);

        if (changeHistory) {
            setEntityHistory([...entityHistory, entity]);
            setEntityHistoryIndex(entityHistory.length);
        }

        searchParams.set("selectedEntityType", entity.entityType || entity.class || entity.getEntityClass().type.name);
        searchParams.set("selectedEntity", entity._id);
        navigate(`/map-and-image?${searchParams.toString()}`)

        if (entity) {
            setObjectPanelExpanded(true);
        }
    }

    const _poisChanged = (pois: any[]) => {
        console.log('POIs changed >>>', pois);

        setPoIs(pois);
    }

    const _propertiesTitle = (entity: any) => {
        if (!entity) {
            return <></>;
        }

        switch (entity.getTypeName ? (entity as Entity).getTypeName() : entity.class) {
            case 'Building': return <div></div>;
            case 'ChargingStation': return <div>
                <div>
                    <div className="text-sm">{entity.chargingType === ChargingType.normalCharging ? t('ui.chargingStation') : t('ui.fastChargingStation')}</div>
                </div>
                <div>
                    <div className="text-2xl font-semibold">{entity.operator}</div>
                </div>
            </div>;
            case 'Billboard': return <div>
                <div>
                    <div className="text-sm">Anzeigetafel</div>
                </div>
                <div>
                    <div className="text-2xl font-semibold">{entity.operator || 'Unbekannter Betreiber'}</div>
                </div>
            </div>;
            case 'AdvertisementColumn': return <div>
                <div>
                    <div className="text-sm">Litfaßsäule</div>
                </div>
                <div>
                    <div className="text-2xl font-semibold">{entity.operator || 'Unbekannter Betreiber'}</div>
                </div>
            </div>;
            case 'University': return <div>
                <div>
                    <div className="text-sm">Universität</div>
                </div>
                <div>
                    <div className="text-2xl font-semibold">{entity.name}</div>
                </div>
            </div>;
            case 'Area': return <div><div>
                <div className="text-sm">Gebiet</div>
            </div>
                <div>
                    <div className="text-2xl font-semibold">{entity && entity.id ? `${entity.id}` : ''}</div>
                </div>
            </div>;
            case 'Bridge':
            case 'Tile':
            case 'LocalGovernment':
            case 'PostalCode':
            case 'PlotOfLand':
            case 'Way':
            case 'Shape':
            case 'PoI':
            default: {
                const typeLabel = (entity.getTypeClass && entity.getTypeClass())
                    ? entity.getTypeClass().type.label.de
                    : (entity.class || 'Objekt');

                return <div>
                    <div className="text-sm">{typeLabel}</div>
                    <div className="text-2xl font-semibold">{entity && (entity as any).name ? (entity as any).name : 'Unbenannt'}</div>
                </div>
            }
        }
    }

    return (
        <div className="flex h-dvh w-full flex-col">
            <Header showHome={true} title={mapOptions && mapOptions.title ? mapOptions.title : ''}></Header>
            <div className="flex flex-1 gap-4 bg-[var(--customer-primary-lighter)] p-4">
                <div className="min-w-0 flex-1">
                    {(() => {
                        console.log('[MapAndImageView] Debug data:', {
                            mapData: !!mapData,
                            mapOptions: !!mapOptions,
                            mapDataKeys: mapData ? Object.keys(mapData) : 'null',
                            mapOptionsKeys: mapOptions ? Object.keys(mapOptions) : 'null'
                        });

                        if (mapData && mapOptions) {
                            return <MapAndImage setObject={selectEntity} options={mapOptions} showMiniMap={true} data={mapData} pois={pois} query={mapQuery} />;
                        } else {
                            return <div className="flex h-full flex-col justify-center p-5 text-center">
                                <div className="mb-2.5 text-lg">No map data available</div>
                                <div className="mb-2.5">mapData: {mapData ? 'available' : 'missing'}</div>
                                <div className="mb-5">mapOptions: {mapOptions ? 'available' : 'missing'}</div>
                                <div className="mb-5">Please select a map resource from the resources list to view the map.</div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="mx-auto"
                                    onClick={() => {
                                        // Test with sample data
                                        const testData: any = {
                                            entityType: 'PoI',
                                            data: [
                                                {
                                                    _type: 'PoI',
                                                    poiType: 'chargingStation',
                                                    lat: 50.10495071520639,
                                                    lon: 8.670636045048122,
                                                    name: 'Test Charging Station',
                                                    operator: 'Test Operator'
                                                }
                                            ]
                                        };
                                        const testOptions: any = {
                                            entityType: 'PoI',
                                            title: 'Test Map'
                                        };
                                        setMapData(testData);
                                        setMapOptions(testOptions);
                                    }}
                                >
                                    Load Test Data
                                </Button>
                            </div>;
                        }
                    })()}
                </div>
            </div>
        </div>
    );
}
